// src/lib/factsDiff.js — what an agent actually CHANGED, and how gaps get refilled.
//
// OWNER 2026-09-03, on "my version": an agent's version is what they typed; the gaps show the
// card they are looking at; opening another card changes only the gaps; and a better ad that
// appears later can refill the gaps "so they don't want to write everything by a hand."
//
// 🔴 THE DEFECT THIS EXISTS TO PREVENT: the editor prefills every field from the card and sent
// EVERY field on save. So opening card B and pressing Record turned B's backfill into "the agent
// asserted this" — silently, for every field they never touched. Their "version" became whatever
// card they last saved from. Only what DIFFERS from the prefill may leave the form as a claim.
//
// Pure, so the rule is testable without a 3,000-line modal.

const norm = v => (v === undefined || v === null ? '' : String(v))

/**
 * The fields whose value differs from the baseline the form was prefilled with.
 * Returns a new object holding only those keys, with their CURRENT values.
 *
 * ⚠️ A field cleared back to empty IS a change (the agent removed a value) and is returned as
 * '' — the caller decides what an emptied fact means. A field that was empty and stays empty is
 * not a change: nothing was asserted.
 */
export function factsDiff(baseline, current) {
  const out = {}
  const b = baseline || {}, c = current || {}
  for (const k of new Set([...Object.keys(b), ...Object.keys(c)])) {
    if (norm(b[k]) !== norm(c[k])) out[k] = c[k]
  }
  return out
}

/**
 * "Fill my gaps from this card": for every field the agent has NOT written themselves, take the
 * card's value. Fields they own are untouched — that is the whole point of owning them.
 *
 * Returns { next, filled }: the new form values, and the keys that were filled, so the caller can
 * drop those keys from the baseline (making them count as the agent's change on save — the agent
 * pressed the button; adopting the values is their act, and it must be recorded as one).
 *
 * @param current  the form as it stands
 * @param ownKeys  attribute names the agent has hand-written (their version)
 * @param card     the card's values (the merged ad view)
 */
export function refillGaps(current, ownKeys, card, baseline = null) {
  const own = new Set(ownKeys || [])
  const next = { ...(current || {}) }
  const filled = []
  for (const [k, v] of Object.entries(card || {})) {
    if (own.has(k)) continue
    // An UNSAVED edit is the agent's too (review 2026-09-16): typing 4 and then pressing "fill my
    // gaps" must not put the card's 2 back. Owned-by-claim and edited-in-this-form both protect.
    if (baseline && norm(next[k]) !== norm(baseline[k])) continue
    if (norm(v) === '') continue                  // the card has nothing here either
    if (norm(next[k]) === norm(v)) continue       // already showing it
    next[k] = v
    filled.push(k)
  }
  return { next, filled }
}

/**
 * The hand-written fields the agent CLEARED — the ones whose claim must be retracted.
 *
 * OWNER 2026-09-03: "clearing a field removes the claim." Only fields the agent OWNS count:
 * clearing a value that was merely the card's backfill asserts nothing and retracts nothing —
 * there is no claim of theirs to remove, and the gap simply shows the card again. And only a
 * field that HAD a value in the baseline counts: empty-to-empty is not a clearing.
 */
export function clearedOwnKeys(baseline, current, ownKeys) {
  const own = new Set(ownKeys || [])
  const out = []
  for (const k of own) {
    if (norm((baseline || {})[k]) !== '' && norm((current || {})[k]) === '') out.push(k)
  }
  return out
}

/**
 * The form's state AFTER a successful save (review 2026-09-16). Without this the baseline stayed at
 * the original prefill for the whole session: change rooms 2→3, save, change back to 2, save — the
 * second save compared 2 against the ORIGINAL 2 and sent nothing, leaving 3 in the database. And the
 * owned list never grew, so a fact first saved in this session could not be cleared again.
 *
 * `sent` = the facts the save carried (key → value, undefined = not sent); `retracted` = the
 * server's { attribute: count } for cleared fields it removed.
 */
export function rebaseAfterSave({ facts, ownKeys, sent, retracted }) {
  const own = new Set(ownKeys || [])
  for (const [k, v] of Object.entries(sent || {})) if (v !== undefined && norm(v) !== '') own.add(k)
  for (const k of Object.keys(retracted || {})) own.delete(k)
  return { baseline: { ...(facts || {}) }, ownKeys: [...own] }
}

// ── What a save did NOT write (review 2026-09-17) ───────────────────────────────────────────────
// core_ingest reports refused values in `unresolved` under the OBSERVATION's camelCase key
// (`implausible: ['ceilingHeight=12']`, `invalid: ['aptFloor']`) or the claim attribute
// (`claimFailed: ['floor: <message>']`), and core_manual reports refused clears in
// `retract_refused: ['floor: <message>']`. Map both back to the form's keys, so a failed value stays a
// change the next save retries instead of being rebased as if it had landed.
// ⚠️ Mirrors core_prepare FACT_KEYS (form key → observation key). A key missing here is only reported,
// never retried — the safe direction.
const OBSERVATION_TO_FORM = {
  roomsCount: 'rooms', bedroomsCount: 'bedrooms', bathroomCount: 'bathrooms', areaSqM: 'area_sqm',
  landAreaSqM: 'land_area_sqm', kitchenArea: 'kitchen_area', aptFloor: 'floor', totalFloors: 'floors_total',
  ceilingHeight: 'ceiling_height', ceilingHeightMin: 'ceiling_height_min', ceilingHeightMax: 'ceiling_height_max',
  ceilingVariation: 'ceiling_variation', buildYear: 'year_built', hasBalcony: 'has_balcony', balconyCount: 'balcony_count',
  balconyAreaSqM: 'balcony_area_sqm', hasTerrace: 'has_terrace', livingRoomArea: 'living_room_area_sqm',
  loggiaArea: 'loggia_area_sqm', storeroomArea: 'storeroom_area_sqm', porchArea: 'porch_area_sqm',
  materialType: 'building_material', condition: 'condition', status: 'building_status', project: 'project',
  powerCapacityKw: 'power_capacity_kw', functionalZone: 'functional_zone', parkingDeeded: 'parking_deeded',
  metroStation: 'metro_station', landAreaUnit: 'land_area_unit', manualSubtype: 'subtype',
}
const headOf = entry => String(entry ?? '').split(/[:=]/)[0].trim()
const toFormKey = k => OBSERVATION_TO_FORM[k] || k

export function failedFactKeys(r) {
  const u = (r && r.unresolved) || {}
  const failed = new Set()
  for (const e of [...(u.implausible || []), ...(u.invalid || [])]) failed.add(toFormKey(headOf(e)))
  for (const e of u.claimFailed || []) failed.add(toFormKey(headOf(e)))
  const retractFailed = new Set((r && Array.isArray(r.retract_refused) ? r.retract_refused : []).map(e => headOf(e)))
  return { failed, retractFailed }
}

/**
 * rebaseAfterSave, failure-aware: a value the server refused keeps the PREVIOUS baseline (so it is still a
 * change and the next save retries it) and is not counted as the agent's own. Same for a clear the server
 * refused to remove.
 */
export function rebaseAfterSaveWithFailures({ facts, prevBaseline, ownKeys, sent, retracted, failed, retractFailed }) {
  const bad = new Set([...(failed || []), ...(retractFailed || [])])
  const okSent = Object.fromEntries(Object.entries(sent || {}).filter(([k]) => !bad.has(k)))
  const rb = rebaseAfterSave({ facts, ownKeys, sent: okSent, retracted })
  for (const k of bad) {
    if (prevBaseline && k in prevBaseline) rb.baseline[k] = prevBaseline[k]
    else delete rb.baseline[k]
  }
  return { ...rb, retry: bad.size > 0 }
}
